import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { productKeys } from '@/hooks/useProducts';
import { getCatalogProduct } from '@/api/directSale';
import { useProductSelectionStore } from '@/stores/productSelectionStore';
import { useDirectSaleStore } from '@/stores/directSaleStore';
import type { Product } from '@/api/products';
import { sizesForColor } from './direct-sale-sizes';
import {
  effectiveMethodId,
  methodsForProduct,
  withEffectiveMethod,
} from './direct-sale-methods';
import { useDirectSaleMethod } from './useDirectSaleMethod';

/** The editor's view of the product: the chosen method applied. */
function seedEditor(
  queryClient: ReturnType<typeof useQueryClient>,
  product: Product,
  methodId: string | null
) {
  const shaped = withEffectiveMethod(product, methodId);
  queryClient.setQueryData<Product[]>(productKeys.list(undefined), [shaped]);
  queryClient.setQueryData<Product>(productKeys.detail(shaped.id), shaped);
}

/**
 * Drives the shared editor core from Direct Sale state.
 *
 * The editor (ProductPreview → CanvasEditor) takes no props worth speaking
 * of; it reads everything from the Seller flow's stores. Rather than rewrite
 * 1500 lines of working editor code, Direct Sale mirrors its own state into
 * those stores and lets the editor run unchanged.
 *
 * Two mismatches have to be bridged:
 *
 * 1. **Selection shape.** The Seller flow models "several products, several
 *    colours each". Direct Sale is one product and one colour, expressed in
 *    the same structure.
 *
 * 2. **Data source.** ProductPreview calls useProducts(), which hits the
 *    authenticated product endpoint — a Direct Sale customer has no token and
 *    would get a 401. The bridge fetches from the session-bound Direct Sale
 *    catalogue endpoint (which answers with the embedding shop's products)
 *    and seeds the result into the cache key useProducts() reads, so the
 *    editor resolves it from cache and never issues the authenticated call.
 *
 * 3. **Decoration method.** The editor derives embroidery (layering,
 *    thread limits) from each position's method. Direct Sale lets the
 *    customer choose one, so the seeded product carries the chosen method
 *    on every position that offers it; the Seller Creator never sees it.
 *
 * This coupling is the known cost of not extracting the editor core. It lives
 * in one file on purpose: when the core is extracted, this is what gets
 * deleted.
 */
export function useDirectSaleEditorBridge() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const productId = useDirectSaleStore((s) => s.productId);
  const colorId = useDirectSaleStore((s) => s.colorId);
  const setColor = useDirectSaleStore((s) => s.setColor);
  const sizeId = useDirectSaleStore((s) => s.sizeId);
  const quantities = useDirectSaleStore((s) => s.quantities);

  const {
    data: product,
    isPending,
    isError,
    error,
  } = useQuery({
    queryKey: ['direct-sale', 'product', productId],
    // Seeding happens INSIDE the queryFn, before the first render that
    // mounts ProductPreview. An effect would be too late: child effects run
    // before parent effects, so ProductPreview's useProducts() would fire
    // the authenticated GET /products first — and a Direct Sale customer
    // has no token, which sends the iframe through the 401 login redirect.
    queryFn: async () => {
      const loaded = await getCatalogProduct(productId as string);
      // Seeded with the method already chosen (resumed work), so the
      // editor does not first lay out the default and then rebuild.
      const { decorationMethodId, decoratedPositions } =
        useDirectSaleStore.getState();
      seedEditor(
        queryClient,
        loaded,
        effectiveMethodId(
          methodsForProduct(
            loaded,
            decoratedPositions.map((position) => position.positionId)
          ),
          decorationMethodId
        )
      );
      return loaded;
    },
    enabled: productId !== null,
  });

  // A changed choice reaches the editor through the same cache.
  const { methods, effectiveId } = useDirectSaleMethod(product);
  const seeded = useRef<{ product: Product; methodId: string | null } | null>(
    null
  );
  useEffect(() => {
    if (!product) return;
    const last = seeded.current;
    if (last?.product === product && last.methodId === effectiveId) return;
    seeded.current = { product, methodId: effectiveId };
    seedEditor(queryClient, product, effectiveId);
  }, [product, effectiveId, queryClient]);

  // A newly decorated position that cannot take the chosen method sends
  // every position back to its default. Said once, and the pick is
  // dropped, so it does not silently return when that position is cleared.
  const pickedId = useDirectSaleStore((s) => s.decorationMethodId);
  const setDecorationMethod = useDirectSaleStore((s) => s.setDecorationMethod);
  const lastEffective = useRef<string | null>(null);
  const pickedName = useRef<string | null>(null);
  useEffect(() => {
    const previous = lastEffective.current;
    lastEffective.current = effectiveId;
    if (effectiveId) {
      pickedName.current =
        methods.find((method) => method.id === effectiveId)?.name ?? null;
    }
    if (!product || !pickedId || effectiveId !== null) return;
    if (previous !== pickedId) return;
    toast(
      t('directSale.design.methodReset', {
        method: pickedName.current ?? '',
      })
    );
    setDecorationMethod(null);
  }, [product, pickedId, effectiveId, methods, setDecorationMethod, t]);

  // Default to the product's first colour so the editor always has a mockup
  // to render — an unset colour would leave the canvas blank with no hint why.
  useEffect(() => {
    if (!product || colorId) return;
    const first = product.colors?.[0];
    if (first) setColor(first.id);
  }, [product, colorId, setColor]);

  // Resumed work can hold a size the colour no longer offers (discontinued
  // since, or saved by a bundle that listed every colour's sizes). No
  // stepper shows it, so the customer could never remove it, and every
  // quote would fail with quote_size_unknown. Prune through setColor, the
  // same path a colour change takes.
  useEffect(() => {
    if (!product || !colorId) return;
    const offered = sizesForColor(product, colorId).map((size) => size.id);
    const stale =
      (sizeId !== null && !offered.includes(sizeId)) ||
      Object.keys(quantities).some((id) => !offered.includes(id));
    if (stale) setColor(colorId, offered);
  }, [product, colorId, sizeId, quantities, setColor]);

  // Mirror the one-product-one-colour selection into the Seller-shaped store.
  useEffect(() => {
    if (!product) return;

    const selection = useProductSelectionStore.getState();
    const activeColor = colorId ?? product.colors?.[0]?.id ?? null;

    selection.toggleProduct(product.id, true);
    if (activeColor) {
      selection.selectAllColors(product.id, [activeColor]);
    }
    selection.setFocusedProduct(product.id, activeColor);
  }, [product, colorId]);

  // Direct Sale owns one product per session; leaving the selection behind
  // would surface it in any Seller view opened in the same tab.
  useEffect(
    () => () => {
      useProductSelectionStore.getState().reset();
    },
    []
  );

  return { product, isPending, isError, error };
}
